import { CABECERA, pt, TINTA } from "./medidas"
import { Caja, Marca, Rotulo } from "./piezas"
import logoSeniat from "./seniat.png"
import type { Comun, Regimen } from "./tipos"

/**
 * La cabecera de los formatos de la Aduana Principal de Puerto Cabello.
 *
 * ⚠ **Es la misma en los dos formatos hasta el píxel**, así que vive aquí y no
 * duplicada. Lo único que cambia es el título. El día que la aduana publique un
 * tercer formato con esta cabecera —que es lo que ha hecho con estos dos— se
 * reutiliza entera.
 *
 * ⚠ **El logotipo del SENIAT se sirve como imagen y NO se redibuja.** Es la marca de
 * un organismo del Estado: reproducirla a mano con trazos propios sería fabricar una
 * versión no oficial de un escudo oficial. Sale del PDF que publica la propia aduana,
 * a su tamaño (94,6 × 43,5 pt).
 *
 * ⚠ **Se IMPORTA, y no vive en `public/`.** Estuvo en `public/plantillas/seniat.png`,
 * lo que creaba una carpeta `/plantillas/` en el build con el mismo nombre que la
 * ruta de esta pantalla: nginx la encontraba antes que el SPA y respondía 403. Al
 * importarla, Vite la emite en `/assets/` con un hash en el nombre, así que no puede
 * chocar con ninguna ruta y el navegador deja de servir la vieja si algún día
 * cambia.
 *
 * ⚠ **Las ocho casillas de régimen no se explican en el formato, y tampoco aquí.**
 * ENA, RE, IMA, AT, IM, ID, RI y TR son los códigos que usa la aduana; inventarles
 * una leyenda sería poner en un documento oficial un texto que no le pertenece.
 */
export function CabeceraSeniat({
  titulo,
  datos,
  onCambio,
}: {
  titulo: string
  datos: Comun
  onCambio: (cambio: Partial<Comun>) => void
}) {
  function marcar(clave: Regimen, marcada: boolean) {
    onCambio({ regimenes: { ...datos.regimenes, [clave]: marcada } })
  }

  return (
    <>
      {/* El panel redondeado. El radio se midió sobre el original: 5 pt. */}
      <div
        style={{
          position: "absolute",
          left: `${pt(CABECERA.panel.x)}mm`,
          top: `${pt(CABECERA.panel.y)}mm`,
          width: `${pt(CABECERA.panel.w)}mm`,
          height: `${pt(CABECERA.panel.h)}mm`,
          background: TINTA.panel,
          borderRadius: `${pt(5)}mm`,
        }}
      />

      <img
        src={logoSeniat}
        alt="SENIAT · Servicio Nacional Integrado de Administración Aduanera y Tributaria"
        style={{
          position: "absolute",
          left: `${pt(CABECERA.logo.x)}mm`,
          top: `${pt(CABECERA.logo.y)}mm`,
          width: `${pt(CABECERA.logo.w)}mm`,
          height: `${pt(CABECERA.logo.h)}mm`,
        }}
      />

      <Rotulo x={CABECERA.aduana.x} y={CABECERA.aduana.y} tamano={8} tinta={TINTA.oscura}>
        Aduana Principal de Puerto Cabello
      </Rotulo>

      <Rotulo
        x={CABECERA.titulo.x}
        y={CABECERA.titulo.y}
        tamano={14.1}
        centrado={CABECERA.titulo.w}
        tinta={TINTA.oscura}
        negrita={true}
      >
        {titulo}
      </Rotulo>

      {CABECERA.casillas.map((casilla) => (
        <span key={casilla.clave}>
          <Rotulo x={casilla.etiqueta.x} y={casilla.etiqueta.y} tinta={TINTA.oscura}>
            {casilla.clave}
          </Rotulo>
          <Marca
            x={casilla.caja.x}
            y={casilla.caja.y}
            w={CABECERA.casilla.w}
            h={CABECERA.casilla.h}
            etiqueta={casilla.clave}
            marcada={datos.regimenes[casilla.clave]}
            onCambio={(v) => marcar(casilla.clave, v)}
          />
        </span>
      ))}

      <Rotulo x={CABECERA.fechaRotulo.x} y={CABECERA.fechaRotulo.y}>
        FECHA DE SOLICITUD
      </Rotulo>
      {(["dia", "mes", "anio"] as const).map((parte) => {
        const sitio = CABECERA.fecha[parte]
        // ⚠ El formato escribe «Dia» sin tilde, y así se reproduce. Lo que sí va
        // bien escrito es el nombre accesible: ése es nuestro, no del SENIAT.
        const impreso = parte === "dia" ? "Dia" : parte === "mes" ? "Mes" : "Año"
        const nombre = parte === "dia" ? "Día" : impreso
        return (
          <span key={parte}>
            <Rotulo x={sitio.micro.x} y={sitio.micro.y} tamano={7}>
              {impreso}
            </Rotulo>
            <Caja
              x={sitio.caja.x}
              y={sitio.caja.y}
              w={sitio.caja.w}
              h={sitio.caja.h}
              etiqueta={`${nombre} de la fecha de solicitud`}
              centrado={true}
              valor={datos.fecha_solicitud[parte]}
              onCambio={(v) =>
                onCambio({ fecha_solicitud: { ...datos.fecha_solicitud, [parte]: v } })
              }
            />
          </span>
        )
      })}
    </>
  )
}
